import { Select as SelectPrimitive } from "@base-ui/react/select";
import { IconBuildingStore, IconChevronDown } from "@tabler/icons-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { Select, SelectContent, SelectGroup, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { cn } from "@/lib/utils";
import type { Shop } from "@/stores/auth";
import { ShopComparisonChart } from "./shop-comparison-chart";
import {
  buildShopComparisonRows,
  mergeShopSelection,
  SHOP_COMPARISON_COLORS,
  SHOP_COMPARISON_MAX_SHOPS,
  SHOP_COMPARISON_METRIC_FORMAT,
  SHOP_COMPARISON_METRICS,
  type ShopComparisonMetric,
  type ShopComparisonSeries,
  type ShopComparisonShop,
  useShopComparisonSeries,
  useShopComparisonShops,
} from "./shop-comparison-data";

export interface ShopComparisonCardProps {
  /** Shops the picker offers (the analytics scope), in display order. */
  shops: ShopComparisonShop[];
  /** Compared shops, 1–4; the position picks the series color (chart-1…chart-4). */
  selectedShopIds: string[];
  onSelectedShopIdsChange: (shopIds: string[]) => void;
  metric: ShopComparisonMetric;
  onMetricChange: (metric: ShopComparisonMetric) => void;
  /** Weekly analytics per selected shop (any order; matched by shopId). */
  series: ShopComparisonSeries[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  className?: string;
}

/** Borderless filled trigger from the dashboard card header (Paper "Metric select"). */
const subtleTriggerClassName =
  "flex h-7 items-center gap-1.5 rounded-sm border border-transparent bg-control-subtle pr-2 pl-2.5 text-xs font-medium whitespace-nowrap text-text-secondary outline-none select-none ring-ring/15 transition-[background-color,border-color,box-shadow] duration-[var(--duration-fast)] hover:bg-control-subtle-hover active:bg-control-subtle-pressed focus-visible:border-ring focus-visible:ring-2 aria-expanded:bg-control-subtle-hover disabled:cursor-not-allowed disabled:opacity-60";

function TriggerChevron() {
  return (
    <SelectPrimitive.Icon
      render={<IconChevronDown aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />}
    />
  );
}

/** One metric across the compared shops, one line per shop, by week. */
export function ShopComparisonCard({
  shops,
  selectedShopIds,
  onSelectedShopIdsChange,
  metric,
  onMetricChange,
  series,
  isLoading,
  isError,
  onRetry,
  className,
}: ShopComparisonCardProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const titleId = useId();
  // Same names as the KPI strip, so "ACoS" reads the same everywhere on the page.
  const metricLabel = (key: ShopComparisonMetric) => t(`overview.kpi.metrics.${key}`);
  const shopById = new Map(shops.map((shop) => [shop.shopId, shop]));
  const selectedShops = selectedShopIds.flatMap((shopId, index) => {
    const shop = shopById.get(shopId);
    const color = SHOP_COMPARISON_COLORS[index];
    return shop && color ? [{ ...shop, color }] : [];
  });
  const seriesById = new Map(series.map((item) => [item.shopId, item]));
  const orderedSeries = selectedShops.map((shop) => seriesById.get(shop.shopId));
  const rows = buildShopComparisonRows(orderedSeries, metric);
  const format = SHOP_COMPARISON_METRIC_FORMAT[metric];
  const currencies = new Set(orderedSeries.map((item) => item?.currency ?? null));
  const mixedCurrency = format === "currency" && currencies.size > 1;
  const axisCurrency = mixedCurrency ? null : (orderedSeries[0]?.currency ?? null);
  const hasData = rows.length > 0;
  const atLimit = selectedShopIds.length >= SHOP_COMPARISON_MAX_SHOPS;
  const pickerLabel =
    selectedShopIds.length === shops.length
      ? t("overview.shopComparison.allShops", { count: shops.length })
      : t("overview.shopComparison.shopsSelected", {
          selected: selectedShopIds.length,
          total: shops.length,
        });

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        "flex min-w-0 flex-col rounded-lg bg-surface shadow-[var(--paper-raised-shadow)]",
        className,
      )}
    >
      <header className="flex items-center justify-between gap-3 pt-4 pr-4 pl-5">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 id={titleId} className="truncate text-base font-semibold text-ink">
            {t("overview.shopComparison.title")}
          </h2>
          <span className="shrink-0 text-xs text-muted-foreground">
            {t("overview.shopComparison.weekly")}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Select<ShopComparisonMetric>
            value={metric}
            onValueChange={(next) => {
              if (next) onMetricChange(next);
            }}
          >
            <SelectPrimitive.Trigger
              aria-label={`${t("overview.shopComparison.metric")}: ${metricLabel(metric)}`}
              className={subtleTriggerClassName}
            >
              <SelectPrimitive.Value>
                {(value: ShopComparisonMetric) => metricLabel(value)}
              </SelectPrimitive.Value>
              <TriggerChevron />
            </SelectPrimitive.Trigger>
            <SelectContent align="end" className="w-auto min-w-36">
              <SelectGroup>
                {SHOP_COMPARISON_METRICS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {metricLabel(key)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select<string, true>
            multiple
            value={selectedShopIds}
            onValueChange={(next) => {
              // The comparison always keeps at least one shop.
              if (next.length > 0)
                onSelectedShopIdsChange(mergeShopSelection(selectedShopIds, next));
            }}
          >
            <SelectPrimitive.Trigger
              aria-label={`${t("overview.shopComparison.shops")}: ${pickerLabel}`}
              className={subtleTriggerClassName}
            >
              <IconBuildingStore
                aria-hidden
                className="size-[13px] shrink-0 text-muted-foreground"
              />
              <span className="tabular-nums">{pickerLabel}</span>
              <TriggerChevron />
            </SelectPrimitive.Trigger>
            {/* A fixed cap keeps the list shorter than the space on either side, so placement is
                decided once; letting it grow to the available height made it flip mid-open. */}
            <SelectContent align="end" className="max-h-80 w-auto min-w-56">
              <SelectGroup>
                {shops.map((shop) => {
                  const selected = selectedShopIds.includes(shop.shopId);
                  return (
                    <SelectItem
                      key={shop.shopId}
                      value={shop.shopId}
                      disabled={!selected && atLimit}
                      // Several rows are selected at once; the check alone marks them.
                      className="data-selected:not-data-highlighted:bg-transparent"
                    >
                      <span className="truncate">{shop.name}</span>
                      {shop.marketplace ? (
                        <span className="text-micro text-muted-foreground">{shop.marketplace}</span>
                      ) : null}
                    </SelectItem>
                  );
                })}
              </SelectGroup>
              {atLimit && shops.length > SHOP_COMPARISON_MAX_SHOPS ? (
                <p className="px-2.5 pt-1 pb-1.5 text-badge text-muted-foreground">
                  {t("overview.shopComparison.limit", { count: SHOP_COMPARISON_MAX_SHOPS })}
                </p>
              ) : null}
            </SelectContent>
          </Select>
        </div>
      </header>
      <div className="flex flex-col gap-2.5 px-5 pt-3 pb-[18px]">
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1.5">
          {mixedCurrency ? (
            <span className="mr-auto text-badge text-muted-foreground">
              {t("overview.shopComparison.mixedCurrency")}
            </span>
          ) : null}
          {selectedShops.map((shop) => (
            <span key={shop.shopId} className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-[3px]"
                style={{ backgroundColor: shop.color }}
              />
              <span className="text-xs text-text-secondary">{shop.name}</span>
              {shop.marketplace ? (
                <span className="text-micro text-muted-foreground">{shop.marketplace}</span>
              ) : null}
            </span>
          ))}
        </div>
        {isError && !hasData ? (
          <div className="flex h-[236px] items-center justify-center">
            <ErrorState message={t("overview.shopComparison.loadFailed")} onRetry={onRetry} />
          </div>
        ) : isLoading && !hasData ? (
          <Skeleton className="h-[236px] w-full rounded-md" />
        ) : !hasData ? (
          <p className="flex h-[236px] items-center justify-center text-xs text-muted-foreground">
            {t("overview.shopComparison.empty")}
          </p>
        ) : (
          <>
            <ShopComparisonChart
              rows={rows}
              series={selectedShops.map((shop, index) => ({
                shopId: shop.shopId,
                name: shop.name,
                color: shop.color,
                currency: orderedSeries[index]?.currency ?? null,
              }))}
              metricLabel={metricLabel(metric)}
              format={format}
              axisCurrency={axisCurrency}
            />
            {isError ? (
              <ErrorState
                variant="inline"
                message={t("overview.shopComparison.loadFailed")}
                onRetry={onRetry}
              />
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

export interface ShopComparisonSectionProps {
  workspaceId: string | null;
  /** All shops of the active workspace (`useShopScope().workspaceShops`). */
  workspaceShops: Shop[];
  /** The analytics scope (`useShopScope().scopeShopIds`); the picker offers these shops. */
  scopeShopIds: string[];
  startDate: string;
  endDate: string;
  timeZone: ReportingTimeZone;
  /** Applied ASIN filter; the weekly series count only those ASINs' ad metrics. */
  asins?: string[];
  /** Shop ids ordered by ad activity in the period; the default selection takes the first four. */
  rankedShopIds?: string[];
  className?: string;
}

/**
 * Data-wired card: owns the metric and the compared shops, and loads one
 * weekly analytics series per compared shop.
 */
export function ShopComparisonSection({
  workspaceId,
  workspaceShops,
  scopeShopIds,
  startDate,
  endDate,
  timeZone,
  asins,
  rankedShopIds = [],
  className,
}: ShopComparisonSectionProps): React.ReactElement {
  const shops = useShopComparisonShops(workspaceShops, scopeShopIds, workspaceId);
  const [metric, setMetric] = useState<ShopComparisonMetric>("acos");
  const [picked, setPicked] = useState<string[]>([]);
  // A scope change can drop picked shops; with none left, fall back to the busiest shops in
  // scope so the default view isn't four flat lines from shops without ad activity. Under an
  // ASIN filter only shops selling those ASINs qualify; padding with others would only add
  // empty lines.
  const inScope = picked.filter((shopId) => shops.some((shop) => shop.shopId === shopId));
  const ranked = rankedShopIds.filter((shopId) => shops.some((shop) => shop.shopId === shopId));
  const filtering = (asins?.length ?? 0) > 0 && ranked.length > 0;
  const fallback = [
    ...ranked,
    ...(filtering
      ? []
      : shops.map((shop) => shop.shopId).filter((shopId) => !rankedShopIds.includes(shopId))),
  ].slice(0, SHOP_COMPARISON_MAX_SHOPS);
  const selectedShopIds = inScope.length > 0 ? inScope : fallback;
  const { series, isLoading, isError, refetch } = useShopComparisonSeries({
    workspaceId,
    shopIds: selectedShopIds,
    startDate,
    endDate,
    timeZone,
    asins,
  });
  return (
    <ShopComparisonCard
      shops={shops}
      selectedShopIds={selectedShopIds}
      onSelectedShopIdsChange={setPicked}
      metric={metric}
      onMetricChange={setMetric}
      series={series}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      className={className}
    />
  );
}
